/**
 * Адаптация после урока: уровень и распределение маршрутов.
 *
 * Все функции чистые и общие для клиента и сервера: правило пересчёта
 * живёт в одном месте, поэтому локальный прогноз и сохранённое на
 * сервере состояние не расходятся.
 */

import type { StrategyWeights } from './diagnostic';
import type {
  AdaptationState,
  CefrLevel,
  LessonOutcome,
  Strategy,
} from './types';

/** Стартовый уровень новичка: A1. */
export const INITIAL_LEVEL = 1;

export const LEVEL_STEP_UP = 0.1;
export const LEVEL_STEP_DOWN = 0.15;
export const LEVEL_MIN = 0.5;
/** 5.0 и выше — C1: последняя ступень историй («русское слово как аномалия»). */
export const LEVEL_MAX = 5;

export const WEIGHT_STEP = 0.05;
/** Пол и потолок веса: обе ветки маршрута всегда остаются достижимыми. */
export const WEIGHT_MIN = 0.1;
export const WEIGHT_MAX = 0.9;

export const INITIAL_ADAPTATION: AdaptationState = {
  estimatedLevel: INITIAL_LEVEL,
  inductiveWeight: 0.5,
  deductiveWeight: 0.5,
};

/** Ступени CEFR по порядку: индекс + 1 — уровень модели, с которого она берётся. */
const CEFR_SCALE: readonly CefrLevel[] = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/** Непрерывный уровень модели → ступень CEFR (1.0–1.99 — A1 и т. д.). */
export function toCefr(estimatedLevel: number): CefrLevel {
  const index = clamp(Math.floor(estimatedLevel) - 1, 0, CEFR_SCALE.length - 1);
  return CEFR_SCALE[index] ?? 'A1';
}

/** +0.1 за урок без ошибок, −0.15 за урок с ошибкой, в границах шкалы. */
export function updateLevel(currentLevel: number, outcome: LessonOutcome): number {
  const delta = outcome.correct ? LEVEL_STEP_UP : -LEVEL_STEP_DOWN;
  return clamp(currentLevel + delta, LEVEL_MIN, LEVEL_MAX);
}

/**
 * Успех усиливает маршрут урока на 0.05, ошибка ослабляет. Второй вес —
 * дополнение до единицы, поэтому оба остаются в [0.1, 0.9].
 */
export function updateWeights(
  current: StrategyWeights,
  outcome: LessonOutcome,
): StrategyWeights {
  const towardInductive = outcome.strategy === 'INDUCTIVE' ? 1 : -1;
  const success = outcome.correct ? 1 : -1;
  const inductiveWeight = clamp(
    current.inductiveWeight + towardInductive * success * WEIGHT_STEP,
    WEIGHT_MIN,
    WEIGHT_MAX,
  );
  return { inductiveWeight, deductiveWeight: 1 - inductiveWeight };
}

/** Маршрут с большим весом; при равенстве — INDUCTIVE. */
export function nextStrategy(weights: StrategyWeights): Strategy {
  return weights.inductiveWeight >= weights.deductiveWeight
    ? 'INDUCTIVE'
    : 'DEDUCTIVE';
}

/** Полный пересчёт состояния после урока. */
export function applyLessonOutcome(
  state: AdaptationState,
  outcome: LessonOutcome,
): AdaptationState {
  return {
    estimatedLevel: updateLevel(state.estimatedLevel, outcome),
    ...updateWeights(state, outcome),
  };
}
